import api from "../../../services/apiClient";
import API_ENDPOINTS from "../../../config/apiEndpoints";
import { getUserData } from "../../../services/apiService";

export const FOLDER_LIBRARY_CATEGORIES = [
  { value: "eISF", label: "eISF" },
  { value: "ePSF", label: "ePSF" },
];

const basePayload = (userData, overrides = {}) => {
  const session =
    userData?.OrganizationId && userData?.StaffId
      ? userData
      : getUserData() || userData || {};
  return {
    OrganizationIdRef: session.OrganizationId || session.organizationId || "",
    ActionType: "",
    ...overrides,
  };
};

const extractRows = (response) => {
  const data = response?.data;
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.Table)) return data.Table;
  if (Array.isArray(data?.table)) return data.table;
  return [];
};

export const normalizeFolderCategory = (raw) => {
  if (raw == null || String(raw).trim() === "") return "eISF";
  const value = String(raw).trim();
  if (value === "eISF Default Template") return "eISF";
  if (value === "ePSF Default Template") return "ePSF";
  if (value.toLowerCase() === "epsf") return "ePSF";
  if (value.toLowerCase() === "eisf") return "eISF";
  return value;
};

export const folderCategoryToOption = (raw) => {
  const value = normalizeFolderCategory(raw);
  return FOLDER_LIBRARY_CATEGORIES.find((opt) => opt.value === value) || {
    value,
    label: value,
  };
};

export const getFolderLibrarySortTime = (row) => {
  const sortRaw = row?.SystemDateTimeSort || row?.systemDateTimeSort;
  if (sortRaw) {
    const parsed = Date.parse(String(sortRaw));
    if (!Number.isNaN(parsed)) return parsed;
  }
  const display = row?.SystemDateTime || row?.systemDateTime;
  if (!display) return 0;
  const match = String(display).match(
    /^(\d{2})\/(\d{2})\/(\d{4})\s+(\d{1,2}):(\d{2})\s*(AM|PM)$/i,
  );
  if (match) {
    let hour = parseInt(match[4], 10);
    const ampm = match[6].toUpperCase();
    if (ampm === "PM" && hour !== 12) hour += 12;
    if (ampm === "AM" && hour === 12) hour = 0;
    return new Date(
      parseInt(match[3], 10),
      parseInt(match[2], 10) - 1,
      parseInt(match[1], 10),
      hour,
      parseInt(match[5], 10),
    ).getTime();
  }
  const parsed = Date.parse(String(display));
  return Number.isNaN(parsed) ? 0 : parsed;
};

export const sortFolderLibraryRowsNewestFirst = (rows = []) =>
  [...(rows || [])].sort(
    (a, b) => getFolderLibrarySortTime(b) - getFolderLibrarySortTime(a),
  );

export const normalizeFolderLibraryList = (response) =>
  extractRows(response).map((row) => ({
    ...row,
    FolderCategory: normalizeFolderCategory(
      row.FolderCategory || row.TemplateType || row.folderCategory,
    ),
  }));

export const isFolderLibrarySuccess = (response) =>
  response?.success === 1 || response?.success === true || response?.success === "1";

export const isFolderLibraryMutationSuccess = (response) => {
  if (isFolderLibrarySuccess(response)) return true;
  const row = extractRows(response)[0];
  return row?.Success === 1 || row?.Success === "1" || row?.success === 1;
};

export const extractFolderLibraryMessage = (response, fallback = "Request failed.") => {
  const row = extractRows(response)[0];
  return String(row?.MSG || row?.msg || response?.message || fallback).trim();
};

export const fetchFolderLibrary = ({ userData }) =>
  api.post(
    API_ENDPOINTS.ISF.EISF_DEFAULT_FOLDER,
    basePayload(userData, { ActionType: "GetFolderLibrary" }),
  );

export const saveFolderLibraryItem = ({ userData, folderName, folderCategory = "eISF" }) =>
  api.post(
    API_ENDPOINTS.ISF.EISF_DEFAULT_FOLDER,
    basePayload(userData, {
      ActionType: "Save",
      FolderName: folderName,
      TemplateType: normalizeFolderCategory(folderCategory),
    }),
  );

export const updateFolderLibraryItem = ({
  userData,
  folderId,
  folderName,
  folderCategory = "eISF",
}) =>
  api.post(
    API_ENDPOINTS.ISF.EISF_DEFAULT_FOLDER,
    basePayload(userData, {
      ActionType: "Update",
      FolderId: folderId,
      FolderName: folderName,
      TemplateType: normalizeFolderCategory(folderCategory),
    }),
  );

export const deleteFolderLibraryItem = ({ userData, folderId }) =>
  api.post(
    API_ENDPOINTS.ISF.EISF_DEFAULT_FOLDER,
    basePayload(userData, {
      ActionType: "Delete",
      FolderId: folderId,
    }),
  );

export const folderLibraryToOptions = (rows = [], { category } = {}) =>
  (rows || [])
    .filter(
      (row) =>
        !category ||
        normalizeFolderCategory(row.FolderCategory || row.TemplateType) === category,
    )
    .map((row) => ({
      value: row.FolderName || row.folderName || "",
      label: row.FolderName || row.folderName || "",
      folderId: row.FolderId || row.folderId || "",
      folderCategory: normalizeFolderCategory(row.FolderCategory || row.TemplateType),
    }))
    .filter((opt) => opt.value);

/** Rows from GetFolderLibrary for one category (eISF / ePSF). */
export const filterFolderLibraryByCategory = (rows = [], categoryRaw) => {
  const category = normalizeFolderCategory(categoryRaw);
  return (rows || []).filter(
    (row) =>
      normalizeFolderCategory(row.FolderCategory || row.TemplateType) === category,
  );
};

/** Map folder library rows to template editor folder rows (flat roots). */
export function mapFolderLibraryToTemplateFolders(libraryRows = [], categoryRaw) {
  const filtered = filterFolderLibraryByCategory(libraryRows, categoryRaw);
  return filtered.map((row) => {
    const folderId = String(row.FolderId || row.folderId || "").trim();
    return {
      FolderId:
        folderId ||
        (typeof crypto !== "undefined" && crypto.randomUUID
          ? crypto.randomUUID()
          : `${Date.now()}-${Math.random()}`),
      FolderName: String(row.FolderName || row.folderName || "").trim(),
      ParentFolderIdRef: null,
      SystemDateTime: row.SystemDateTime || row.systemDateTime || "",
    };
  });
}
